import { ActivatedRoute } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { debounceTime, filter, skip } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../environments/environment";
import {LocaleService} from "../locale/locale.service";
import { AppService } from '../app.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterBehavior } from 'core/utils/router';

@Injectable({
  providedIn: 'root',
})
export class TitleService {

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private activatedRoute: ActivatedRoute,
    private localeService: LocaleService,
    private title: Title,
    private app: AppService,
    private translateService: TranslateService,
    private meta: Meta,
  ) {
    toObservable(this.app.$langIso).pipe(skip(1)).subscribe(() => {
      this.updateMeta();
    });
  }

  boot() {
    RouterBehavior.getRouteDataChanges().pipe(debounceTime(100), filter(data => !!data)).subscribe((data) => {
      this.updateMeta(data);
    });
  }

  public updateMeta(event?: any): void {
    const currentEvent = event ?? this.getCurrentRouteData();
    if (currentEvent) {
      this.meta.updateTag({
        property: 'og:url',
        content: this.document.location.href
      });
      this.updateMetaTitle(currentEvent?.['metaTitle']);
      this.updateMetaDescription(currentEvent?.['metaDesc'], currentEvent?.['metaTitle']);
      if (this.isEnvironmentProduction) {
        if (currentEvent?.['indexForAtLocale'] && this.localeService.isLocaleAT) {
          this.meta.updateTag({
            name: 'robots',
            property: 'robots',
            content: 'index, follow'
          });
          return;
        }
        if (currentEvent?.['noindex']) {
          this.meta.updateTag({
            name: 'robots',
            property: 'robots',
            content: 'noindex, nofollow'
          });
        } else {
          this.meta.updateTag({
            name: 'robots',
            property: 'robots',
            content: 'index, follow'
          });
        }
      } else {
        this.meta.updateTag({
          name: 'robots',
          property: 'robots',
          content: 'noindex, nofollow'
        });
      }
    }
  }

  public updateMetaTitle(title: string): void {
    const newTitle = title
      ? this.translateService.instant(title)
      : this.translateService.instant('');

    this.title.setTitle(newTitle);

    this.meta.updateTag({
      property: 'og:title',
      content: newTitle
    });

    this.meta.updateTag({
      name: 'title',
      content: newTitle
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: newTitle
    });
  }

  public updateMetaDescription(desc: string, title?: string): void {
    let newDesc = desc
      ? this.translateService.instant(desc)
      : this.translateService.instant('');

    this.meta.updateTag({
      property: 'og:description',
      content: newDesc
    });

    this.meta.updateTag({
      name: 'description',
      content: newDesc
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: newDesc
    });
  }

  public addMetaRobotsFollowTag(): void {
    this.meta.addTag({
      name: 'robots',
      property: 'robots',
      content: 'index, follow'
    });
  }

  public addMetaRobotsNoFollowTag(): void {
    this.meta.addTag({
      name: 'robots',
      property: 'robots',
      content: 'noindex, nofollow'
    });
  }

  get isEnvironmentProduction(): boolean {
    return environment.production;
  }

  private getCurrentRouteData(): any {
    let route: ActivatedRoute | null = this.activatedRoute;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    return route?.snapshot?.data;
  }
}
